import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import useSeo from "../../hooks/useSeo";

const values = [
  { icon: "ri-compass-3-line", title: "以读者为中心", desc: "围绕同人读者的真实需求设计目录与导航，让找作品、追更新都更省心。" },
  { icon: "ri-leaf-line", title: "尊重原著", desc: "坚持内容健康、尊重原著与版权方，只收录有据可查、口碑良好的同人。" },
  { icon: "ri-team-line", title: "社区共建", desc: "依托同人创作者与读者的共同维护，让优质内容能被更多人看见。" },
];

const principles = [
  "收录有据：每条收录都基于社区真实热度与口碑，不做无依据的搬运。",
  "如实更新：作品更新进度、热度与收藏数据如实标注，追更不迷路。",
  "健康导向：内容坚持尊重原著、导向健康，拒绝侵权与不当内容。",
  "持续演进：目录、搜索与社区功能持续迭代，为读者创造更好的追更体验。",
];

const testimonials = [
  { name: "木叶路过的路人甲", role: "长篇爱好者", text: "目录整理得很用心，更新进度一目了然，追更从来没迷路过。" },
  { name: "红瞳记事", role: "同人创作者", text: "作品被认真收录和整理，作为创作者感到踏实与尊重。" },
  { name: "卡卡西式的懒散", role: "治愈向读者", text: "分类细、推荐准，总能在这里找到对味口的治愈同人。" },
];

export default function About() {
  useSeo({
    title: "关于忍界同人志 - 品牌介绍、发展理念与内容原则 | 忍界同人志",
    description:
      "了解忍界同人志的品牌定位、发展理念与内容原则。我们是火影同人爱好者自建的导航站，坚持尊重原著、收录有据，为同人读者提供一站式追更体验。",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于忍界同人志",
      url: "https://hhcypx.cn/about",
      inLanguage: "zh-CN",
      description: "火影同人爱好者自建的导航站，聚合同人作品、合集与社区资源。",
    },
  });

  const [formError, setFormError] = useState("");
  const [formOk, setFormOk] = useState(false);
  const [sending, setSending] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("contact_alt") as string)?.trim() ?? "";
    if (honeypot) {
      setFormOk(true);
      setFormError("");
      form.reset();
      return;
    }

    setSending(true);
    setFormError("");
    try {
      const payload = new FormData(form);
      payload.delete("contact_alt");
      const res = await fetch("https://readdy.ai/api/form/da2jekqamq5jpe1hru50", {
        method: "POST",
        body: payload,
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? "";
      if (!res.ok || !parsed || parsed.code !== "OK") {
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }
      setFormOk(true);
      form.reset();
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <section className="relative overflow-hidden border-b border-background-300/60 bg-background-100">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20abstract%20ninja%20scroll%20and%20fire%20embers%20pattern%2C%20warm%20orange%20glow%20on%20deep%20charcoal%20texture%2C%20subtle%20ancient%20japanese%20motifs%2C%20atmospheric%20and%20minimal&width=1600&height=500&seq=about-banner&orientation=landscape"
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-600">关于我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">关于忍界同人志</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-400">
            我们是一群热爱火影同人的读者与创作者，用目录与导航，为每一个同人爱好者守住一片可以安心追更的地方。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold tracking-widest text-accent-500">OUR STORY</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-300">
              忍界同人志诞生于一次普通的追更经历——当同人作品散落在各个角落、更新进度难以追踪时，我们萌生了「做一个按目录整理同人、方便一站式追更」的想法。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-300">
              于是，我们以「火影忍者同人作品合集、社区推荐与导航目录」为定位，一点点收录、整理、标注同人作品与社区资源。从经典长篇到冷门短篇，从热血战斗到清水治愈，我们希望每一位读者都能在这里快速找到想看的同人内容。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-300/60 bg-background-100 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/15 text-primary-300">
                  <i className={`${v.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-400">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-300/60 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-semibold tracking-widest text-accent-500">PRINCIPLE</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念与内容原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              站点的每一次更新，都遵循同一套朴素的理念：让追更更简单，让好内容被看见。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((p, i) => (
              <div key={p} className="flex gap-4 rounded-lg border border-background-300/60 bg-background-200/40 p-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                <p className="text-sm leading-relaxed text-foreground-300">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <span className="text-xs font-semibold tracking-widest text-accent-500">VOICE</span>
        <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">来自同好们的声音</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-lg border border-background-300/60 bg-background-100 p-5">
              <div className="flex gap-1 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm" />
                ))}
              </div>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-300">“{t.text}”</blockquote>
              <figcaption className="mt-4 border-t border-background-300/60 pt-3">
                <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                <p className="text-xs text-foreground-400">{t.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="border-t border-background-300/60 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-semibold tracking-widest text-accent-500">CONTACT</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-400">
                无论是作品收录申请、内容反馈还是合作建议，欢迎随时联系我们。我们会认真阅读每一封来信。
              </p>
              <div className="mt-6 space-y-3 text-sm text-foreground-400">
                <p className="flex items-center gap-2"><i className="ri-mail-line text-primary-300" />hello@nijitongren.example.com</p>
                <p className="flex items-center gap-2"><i className="ri-time-line text-primary-300" />通常会在 1–2 个工作日内回复</p>
              </div>
            </div>

            <form
              id="contact-form"
              data-readdy-form="contact"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-300/60 bg-background-200/40 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-foreground-400">您的称呼</label>
                  <input id="name" name="name" type="text" required placeholder="如何称呼您" className="w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/60 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-foreground-400">联系邮箱</label>
                  <input id="email" name="email" type="email" required placeholder="you@example.com" className="w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/60 focus:outline-none" />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="subject" className="mb-1.5 block text-xs font-medium text-foreground-400">主题</label>
                <select id="subject" name="subject" className="w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-500/60 focus:outline-none">
                  <option>作品收录申请</option>
                  <option>内容反馈</option>
                  <option>合作建议</option>
                  <option>其他</option>
                </select>
              </div>
              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 block text-xs font-medium text-foreground-400">留言内容</label>
                <textarea id="message" name="message" required maxLength={500} rows={4} placeholder="请描述您的想法…（不超过 500 字）" className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/60 focus:outline-none" />
              </div>
              <input type="text" name="contact_alt" className="phantom-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              {formError && (
                <p className="mt-4 rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-sm text-accent-500">{formError}</p>
              )}
              {formOk && (
                <p className="mt-4 rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-2 text-sm text-primary-300">
                  已收到您的留言，我们会尽快回复，感谢支持！
                </p>
              )}
              <button
                type="submit"
                disabled={sending}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
              >
                <i className="ri-send-plane-line" />
                {sending ? "提交中…" : "发送留言"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}